import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { ErrorAlert } from '../components/ErrorAlert';
import { Schemas } from '../lib/schemas';
import { fetchJson } from '../lib/http';
import { useSession } from '../state/SessionContext';


type Link = { businessId: string; businessName?: string; customerId: string; isDefault: boolean };

export function ContextsPage() {
	const [links, setLinks] = useState<Link[] | null>(null);
	const [error, setError] = useState('');
	const [addCustomerId, setAddCustomerId] = useState('');
	const [addPostal, setAddPostal] = useState('');
	const [busyAdd, setBusyAdd] = useState(false);
	const [busySwitchId, setBusySwitchId] = useState<string | null>(null);
	const { session, setSession } = useSession();

	async function load() {
		try {
			const data = await fetchJson<Link[]>('/portal/contexts');
			setLinks(data);
		} catch (e: any) {
			setError('Failed to load contexts.');
		}
	}

	useEffect(() => { load(); }, []);

	const activeLink = links?.find(l => l.businessId === session?.businessId && l.customerId === session?.customerId);

	async function onAdd(e: React.FormEvent) {
		e.preventDefault();
		setError('');
		const parsed = Schemas.contextAdd.safeParse({ customer_id: addCustomerId.trim(), postal_code: addPostal.trim() });
		if (!parsed.success) { setError('Please provide valid details.'); return; }
		setBusyAdd(true);
		try {
			await fetchJson('/portal/contexts/add', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed.data) });
			setAddCustomerId(''); setAddPostal('');
			await load();
		} catch (e: any) {
			if (e?.status === 429) setError('Too many attempts. Please try again in a few minutes.');
			else setError('We could not add that context. Please verify details and try again.');
		} finally {
			setBusyAdd(false);
		}
	}

	async function onSwitch(customerId: string) {
		setBusySwitchId(customerId);
		setError('');
		const parsed = Schemas.contextSwitch.safeParse({ customer_id: customerId });
		if (!parsed.success) { setError('Invalid selection.'); setBusySwitchId(null); return; }
		try {
			const res = await fetchJson<{ customerId: string; businessId: string; emailVerified: boolean }>(
				'/portal/contexts/switch', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed.data) }
			);
			setSession({ customerId: res.customerId, businessId: res.businessId, emailVerified: res.emailVerified });
			await load();
		} catch (e: any) {
			setError('We could not switch context.');
		} finally {
			setBusySwitchId(null);
		}
	}

	return (
		<Layout>
			{/* Active context indicator (only shown when multiple contexts) */}
			{Array.isArray(links) && links.length > 1 && (
				<div className="mb-6 rounded-2xl border border-sedra/20 bg-sedra/5 p-4">
					<div className="text-sm text-gray-800">
						<span className="font-semibold">Active business:</span> {activeLink?.businessName || session?.businessId || '—'}
						{session?.customerId && (
							<span className="ml-2 text-gray-600">(Customer {session.customerId})</span>
						)}
					</div>
				</div>
			)}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<div className="rounded-2xl border border-gray-200 bg-white elev-1 p-6">
					<h2 className="text-xl font-bold text-gray-900">Linked contexts</h2>
					<div className="mt-4 max-h-[60vh] overflow-auto">
						{links === null ? (
							<div className="space-y-2">
								{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />)}
							</div>
						) : links.length === 0 ? (
							<div className="text-sm text-gray-700">No linked contexts.</div>
						) : (
							<ul className="divide-y divide-gray-100">
								{links.map(l => (
									<li key={`${l.businessId}:${l.customerId}`} className="flex items-center justify-between px-2 py-3">
										<div className="text-sm text-gray-900">
											<div className="font-semibold text-gray-900">{l.businessName || l.businessId}</div>
											<div className="text-gray-600">Customer {l.customerId}</div>
										</div>
										<div className="flex items-center gap-2">
											{l.isDefault && <span className="inline-flex items-center rounded-lg border px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">Default</span>}
											{session?.businessId === l.businessId && session?.customerId === l.customerId && (
												<span className="inline-flex items-center rounded-lg border border-green-600/30 bg-green-50 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-green-700">Active</span>
											)}
											<button onClick={() => onSwitch(l.customerId)} disabled={busySwitchId === l.customerId}
												className="rounded-xl bg-sedra px-3 py-1.5 text-sm font-semibold text-white hover:bg-sedra-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">
												{busySwitchId === l.customerId ? 'Switching…' : 'Switch'}
											</button>
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
					{error && <div className="mt-4"><ErrorAlert message={error} /></div>}
				</div>

				<div className="rounded-2xl border border-gray-200 bg-white elev-1 p-6">
					<h2 className="text-xl font-bold text-gray-900">Add a context</h2>
					<form onSubmit={onAdd} className="mt-4 space-y-4">
						<div>
							<label htmlFor="addCustomerId" className="block text-sm font-semibold text-gray-700">Customer ID</label>
							<input id="addCustomerId" name="addCustomerId" required value={addCustomerId} onChange={e => setAddCustomerId(e.target.value)}
								className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
						</div>
						<div>
							<label htmlFor="addPostal" className="block text-sm font-semibold text-gray-700">ZIP code</label>
							<input id="addPostal" name="addPostal" required value={addPostal} onChange={e => setAddPostal(e.target.value)}
								className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
						</div>
						<div className="pt-2">
							<button type="submit" disabled={busyAdd} className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">
								{busyAdd ? 'Adding…' : 'Add'}
							</button>
						</div>
					</form>
				</div>
			</div>
		</Layout>
	);
} 